/**
 * Route ownership for the ChoreStory Next application shell (#23 / R5.1).
 *
 * The recovered page modules carry hash-style link defaults (`#/today`, `#/dashboard`, …) as PROPS,
 * so the shell is the single owner of what those links actually resolve to. The router is a
 * HashRouter for that reason: the 145 recovered `#/…` defaults keep working, and a direct refresh
 * of any deep route resolves on a plain static host with no server rewrite rule. That matters for
 * F-31, which keeps the Astro marketing image and the SPA image as separate release artifacts.
 *
 * `element` is loaded lazily so the initial route ships only the shell plus one page. The
 * initial-route JavaScript budget in R9.2 is 180 KB gzip.
 */
import { lazy } from 'react';

/**
 * EXPLICIT importers, one per surface. Never a template literal.
 *
 * `lazy(() => import(`../pages/${name}.jsx`))` looks tidier and is a real defect: Vite cannot know
 * which name will be passed, so it bundles EVERY matching file in `../pages/`. Measured 2026-09-06,
 * after test files landed beside the pages: `Home.test.jsx`, `QuestStudio.test.jsx` and the CSSOM
 * test helper they import were all emitted into `dist/`, 459,778 bytes of the 2,082,142-byte
 * production bundle — 22% of the shipped JavaScript was test code.
 *
 * An explicit map has no glob to widen. `app/scripts/check-bundle.mjs` runs after every build and
 * fails if a test payload reappears, so this cannot regress silently.
 */
const PAGE = {
  Approvals: () => import('../pages/Approvals.jsx'),
  Auth: () => import('../pages/Auth.jsx'),
  Dashboard: () => import('../pages/Dashboard.jsx'),
  Display: () => import('../pages/Display.jsx'),
  Family: () => import('../pages/Family.jsx'),
  Home: () => import('../pages/Home.jsx'),
  Insights: () => import('../pages/Insights.jsx'),
  Legal: () => import('../pages/Legal.jsx'),
  Login: () => import('../pages/Login.jsx'),
  Onboarding: () => import('../pages/Onboarding.jsx'),
  PlatformAdmin: () => import('../pages/PlatformAdmin.jsx'),
  QuestStudio: () => import('../pages/QuestStudio.jsx'),
  Settings: () => import('../pages/Settings.jsx'),
  Signup: () => import('../pages/Signup.jsx'),
  Today: () => import('../pages/Today.jsx'),
  Trophies: () => import('../pages/Trophies.jsx'),
  Village: () => import('../pages/Village.jsx'),
  Wallet: () => import('../pages/Wallet.jsx'),
};

const page = (name) => {
  const importer = PAGE[name];
  if (!importer) throw new Error(`no page importer registered for "${name}"`);
  return lazy(importer);
};

/**
 * The PRODUCT routes: 17 of the 18 recovered surfaces. No surface is invented here, and none is
 * silently dropped: a link target with no recovered page is listed in UNBUILT_ROUTES instead, and
 * the one recovered surface that is not a product route is listed in PREVIEW_ONLY_SURFACES below.
 */
export const ROUTES = [
  { path: '/home', name: 'Home', surface: 'Home', element: page('Home') },
  { path: '/login', name: 'Login', surface: 'Login', element: page('Login') },
  { path: '/signup', name: 'Signup', surface: 'Signup', element: page('Signup') },
  { path: '/auth', name: 'Auth', surface: 'Auth', element: page('Auth') },
  { path: '/auth/*', name: 'Auth', surface: 'Auth', element: page('Auth') },
  { path: '/onboarding', name: 'Onboarding', surface: 'Onboarding', element: page('Onboarding') },
  { path: '/today', name: 'Today', surface: 'Today', element: page('Today') },
  { path: '/dashboard', name: 'Dashboard', surface: 'Dashboard', element: page('Dashboard') },
  { path: '/approvals', name: 'Approvals', surface: 'Approvals', element: page('Approvals') },
  { path: '/wallet', name: 'Wallet', surface: 'Wallet', element: page('Wallet') },
  { path: '/village', name: 'Village', surface: 'Village', element: page('Village') },
  { path: '/trophies', name: 'Trophies', surface: 'Trophies', element: page('Trophies') },
  { path: '/insights', name: 'Insights', surface: 'Insights', element: page('Insights') },
  { path: '/family', name: 'Family', surface: 'Family', element: page('Family') },
  { path: '/quest-studio', name: 'QuestStudio', surface: 'Quest Studio', element: page('QuestStudio') },
  { path: '/settings', name: 'Settings', surface: 'Settings', element: page('Settings') },
  { path: '/display', name: 'Display', surface: 'Display', element: page('Display') },
  { path: '/legal', name: 'Legal', surface: 'Legal', element: page('Legal') },
];

/**
 * Recovered surfaces that are previews ONLY: reachable at `#/gallery/<name>`, never at a product path.
 *
 * #583 (design §7.7, decision D8). The operator console is `platform-admin/`, its own app on its own
 * host, reached with an operator session. `#/admin` used to render this recovered mockup inside the
 * family app, with sample tenants and counts that looked like real ones. It is no longer a product
 * route, so `#/admin` is the ordinary unknown-route page (not a redirect to operator sign-in). The
 * mockup is kept as a named preview, behind the preview notice and the mutation guard, so the design
 * stays reviewable. No `LIVE_CONTAINERS` entry may ever point at it.
 */
export const PREVIEW_ONLY_SURFACES = [
  { name: 'PlatformAdmin', surface: 'Platform admin', element: page('PlatformAdmin') },
];

/**
 * The surfaces the preview gallery LISTS and SERVES — one definition, used by both. It is the
 * PREVIEW inventory: every product surface plus the preview-only ones, all 18 recovered surfaces.
 *
 * `/auth/*` shares its surface with `/auth`, so it is filtered out: the gallery must show 18 cards,
 * not 19. Before this existed the index filtered wildcards while `#/gallery/:name` looked names up
 * in the unfiltered `ROUTES`, so the two disagreed about what a gallery surface IS. Nothing was
 * reachable through that gap today — measured 2026-09-11, NO name in `ROUTES` exists only on a
 * wildcard path, and `Auth` resolves to `/auth` either way — but the next wildcard-only entry
 * would be servable at a URL the gallery never lists, which is a strange thing to have to discover.
 */
export const GALLERY_SURFACES = [
  ...ROUTES.filter((route) => !route.path.endsWith('/*')),
  ...PREVIEW_ONLY_SURFACES,
];

/**
 * Link targets that the recovered components default to but that have no recovered surface.
 *
 * These are NOT invented pages and they are NOT silent redirects. The shell renders an honest
 * "not built" boundary so the gap is visible in the running app instead of looking like a 404 or,
 * worse, quietly landing somewhere else. Each one carries the delivery task that owns it.
 */
export const UNBUILT_ROUTES = [
  {
    path: '/notifications',
    label: 'Notifications',
    reason:
      'No notifications surface exists among the 18 recovered screens. Approvals.js and Dashboard.js default notificationsHref to this path.',
    ownedBy: '#33 (R8.2)',
  },
];

/** `#/profile` is an alias the recovered pages use for the profile surface, which is Settings. */
export const ROUTE_ALIASES = [{ from: '/profile', to: '/settings' }];

/**
 * Where the shell sends a visitor who lands on `#/` with no path.
 *
 * `/login` is the app's front door: a returning family signs in, and a new family follows the
 * signup link on that same page. It is deliberately NOT `/home` — that surface is a marketing
 * page duplicating the Astro site, so landing there hides the product (owner decision 2026-09-08).
 */
export const DEFAULT_ROUTE = '/login';
